<template>
  <div class="audit-reconcile-cell">

    <!-- Read-only: manager access required -->
    <el-tooltip v-if="readOnly" content="Manager access required to reconcile." placement="top">
      <span class="audit-reconcile-cell__readonly">—</span>
    </el-tooltip>

    <!-- Editable: popover with per-issue reconcile blocks -->
    <el-popover
      v-else
      ref="popover"
      trigger="click"
      placement="left-start"
      :width="320">

      <div class="audit-reconcile-cell__popover">

        <!-- Phase 0: no_rate — assign rate block -->
        <div v-if="issueTypes.indexOf('no_rate') >= 0" class="audit-reconcile-cell__block">
          <p class="audit-reconcile-cell__label">Assign rate</p>

          <!-- Suggested rate shortcut -->
          <div v-if="row.suggestedRate" class="audit-reconcile-cell__suggested">
            <el-button
              size="mini"
              type="success"
              icon="fa fa-check"
              @click="acceptSuggested">
              Accept suggested: {{ row.suggestedRate.name }}
            </el-button>
          </div>

          <select-sharer-rates
            ref="rateSelect"
            :sharer-id="row.organisation && row.organisation.id"
            v-model="rateId"
            placeholder="Choose a rate…"
            label=""
            :no-form="true" />

          <div class="audit-reconcile-cell__footer">
            <el-button
              type="primary"
              size="mini"
              :disabled="!rateId"
              @click="assign">
              Assign rate
            </el-button>
          </div>
        </div>

        <!-- Phase 1: no_start_anchor — set agreement start date -->
        <div v-if="issueTypes.indexOf('no_start_anchor') >= 0" class="audit-reconcile-cell__block">
          <p class="audit-reconcile-cell__label">Set agreement start date</p>
          <date-input
            :no-form="true"
            v-model="startedDate"
            label=""
            placeholder="Pick start date…" />
          <div class="audit-reconcile-cell__footer">
            <el-button
              type="primary"
              size="mini"
              :disabled="!startedDate"
              @click="setStarted">
              Set start date
            </el-button>
          </div>
        </div>

        <!-- Phase 1: no_billing_start — set billing start date -->
        <div v-if="issueTypes.indexOf('no_billing_start') >= 0" class="audit-reconcile-cell__block">
          <p class="audit-reconcile-cell__label">Set billing start date</p>
          <date-input
            :no-form="true"
            v-model="billingStartDate"
            label=""
            placeholder="Pick billing start date…" />
          <div class="audit-reconcile-cell__footer">
            <el-button
              type="primary"
              size="mini"
              :disabled="!billingStartDate"
              @click="setBillingStart">
              Set billing start
            </el-button>
          </div>
        </div>

        <!-- Phase 1: expired_agreement / expiring_soon — extend or terminate -->
        <!-- Rendered once if either type is present (de-duped) -->
        <div
          v-if="issueTypes.indexOf('expired_agreement') >= 0 || issueTypes.indexOf('expiring_soon') >= 0"
          class="audit-reconcile-cell__block">
          <p class="audit-reconcile-cell__label">Extend agreement</p>
          <date-input
            :no-form="true"
            v-model="expiryDate"
            label=""
            placeholder="Pick new expiry date…" />
          <div class="audit-reconcile-cell__footer audit-reconcile-cell__footer--spread">
            <el-button
              type="primary"
              size="mini"
              :disabled="!expiryDate"
              @click="extendExpiry">
              Extend
            </el-button>
            <el-button
              type="danger"
              size="mini"
              @click="softTerminate">
              Terminate (soft-end)
            </el-button>
          </div>
        </div>

        <!-- Phase 2: empty_override / override_without_rate — clear rate override -->
        <!-- Rendered once for either slug (de-duped to one block) -->
        <div
          v-if="issueTypes.indexOf('empty_override') >= 0 || issueTypes.indexOf('override_without_rate') >= 0"
          class="audit-reconcile-cell__block">
          <p class="audit-reconcile-cell__label">Clear rate override</p>
          <p class="audit-reconcile-cell__hint">Remove the manual override so billing resumes from the calculated rate.</p>
          <div class="audit-reconcile-cell__footer">
            <el-button
              type="warning"
              size="mini"
              @click="clearOverride">
              Clear override
            </el-button>
          </div>
        </div>

        <!-- Phase 2: terminated_no_end — set termination end date -->
        <div v-if="issueTypes.indexOf('terminated_no_end') >= 0" class="audit-reconcile-cell__block">
          <p class="audit-reconcile-cell__label">Set termination end date</p>
          <date-input
            :no-form="true"
            v-model="endedDate"
            label=""
            placeholder="Pick end date…" />
          <div class="audit-reconcile-cell__footer">
            <el-button
              type="primary"
              size="mini"
              :disabled="!endedDate"
              @click="setEndDate">
              Set end date
            </el-button>
          </div>
        </div>

        <!-- Phase 2 + XOL-376 Inc 4 + XOL-416: advisory block — orphan_version_pin,
             no_structural_match, missing_structure_height, first_sharer_only_violation,
             ambiguous_billing_mode, duplicate_sharer_no, zero_sharer_no (no one-click fix) -->
        <div v-if="advisoryIssues.length > 0" class="audit-reconcile-cell__block audit-reconcile-cell__block--advisory">
          <p class="audit-reconcile-cell__label audit-reconcile-cell__label--muted">Needs manual review</p>
          <ul class="audit-reconcile-cell__advisory-list">
            <li
              v-for="issue in advisoryIssues"
              :key="issue.type"
              class="audit-reconcile-cell__advisory-item">
              {{ issue.detail || issue.type }}
            </li>
          </ul>
          <div v-if="row.site && row.site.id" class="audit-reconcile-cell__advisory-footer">
            <router-link
              :to="'/sites/d.site.' + row.site.id + '.finance.sharers'"
              target="_blank"
              class="audit-reconcile-cell__open-site">
              Open site ↗
            </router-link>
          </div>
        </div>

      </div>

      <!-- Trigger button -->
      <el-button
        slot="reference"
        size="mini"
        type="primary"
        icon="fa fa-wrench">
        Reconcile
      </el-button>

    </el-popover>

  </div>
</template>

<script>

import axios from '@/lib/axiosClient'
import PostMixins from '@/mixins/PostMixins'
import NumberMixins from '@/mixins/NumberMixins'
import DateMixins from '@/mixins/DateMixins'

import SelectSharerRates from '@/components/atoms/selects/SelectSharerRates'
import DateInput from '@/components/atoms/inputs/DateInput'

// Phase 2 + XOL-376 Inc 4 + XOL-416: issue types that require manual review —
// no one-click reconcile action.
//
// This list encodes ACTIONABILITY, not severity. `duplicate_sharer_no` and
// `zero_sharer_no` are severity `error` (silent non-billing and a shared telco
// column are money defects) yet still belong here: only a super can decide which
// sharer keeps a contested slot, or what number an unassigned one should take.
// An error with no safe auto-resolve is exactly what "Needs manual review" is for.
var ADVISORY_TYPES = ['orphan_version_pin', 'no_structural_match', 'missing_structure_height', 'first_sharer_only_violation', 'ambiguous_billing_mode', 'duplicate_sharer_no', 'zero_sharer_no']

export default {
  name: 'audit-reconcile-cell',

  mixins: [PostMixins, NumberMixins, DateMixins],

  components: {
    SelectSharerRates,
    DateInput
  },

  props: {
    row: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    // Phase 0: /licensees/no-rate rows have no issues[] — fall back to
    // the implicit no_rate issue so the assign-rate block always renders.
    // Phase 1 /licensees/audit rows will carry real issues[], which take over.
    effectiveIssues: function () {
      return (this.row.issues && this.row.issues.length)
        ? this.row.issues
        : [{ type: 'no_rate' }]
    },

    issueTypes: function () {
      return this.effectiveIssues.map(function (i) { return i.type })
    },

    // Phase 2: filter effectiveIssues to advisory-only types for the advisory block.
    advisoryIssues: function () {
      return this.effectiveIssues.filter(function (i) {
        return ADVISORY_TYPES.indexOf(i.type) >= 0
      })
    }
  },

  methods: {
    // Accept the backend-suggested rate directly (seeds rateId + triggers assign).
    acceptSuggested: function () {
      if (this.row.suggestedRate) {
        this.rateId = this.row.suggestedRate.id
        this.assign()
      }
    },

    // Resolve the display name for the selected rate.
    // Prefer the suggestedRate name when the ids match; otherwise look up
    // from the SelectSharerRates options list via the component ref.
    resolveRateName: function () {
      var id = this.rateId
      if (this.row.suggestedRate && this.row.suggestedRate.id === id) {
        return this.row.suggestedRate.name
      }
      var selectRef = this.$refs.rateSelect
      if (selectRef && selectRef.options) {
        var match = selectRef.options.find(function (o) { return o.id === id })
        if (match) return match.name
      }
      return 'the selected rate'
    },

    // Shared confirm + saveForm helper. Reduces per-action boilerplate.
    // opts: { title, message, confirmText, formData }
    // successCb is called on success (before emit + close).
    doReconcile: function (opts) {
      var self = this
      this.$confirm(opts.message, opts.title, {
        confirmButtonText: opts.confirmText || 'Confirm',
        cancelButtonText: 'Cancel',
        type: opts.type || 'warning',
        center: true
      }).then(function () {
        self.saveForm(
          self,
          '/licensees',
          self.row.licensee_id,
          opts.formData,
          'licensee',
          function (data) {
            self.$emit('resolved')
            if (self.$refs.popover) {
              self.$refs.popover.doClose()
            }
          },
          null,
          true,
          'audittable'
        )
      }).catch(function () {
        // Confirm cancelled — no-op
      })
    },

    assign: function () {
      if (!this.rateId) return

      var self = this
      var rateName = this.resolveRateName()
      var orgName = this.row.organisation && this.row.organisation.name
      var siteCode = this.row.site && this.row.site.siteId
      var legacyBill = this.formatMoney(this.row.currentLegacyBill || 0, 2)

      var message = 'Assign "' + rateName + '" to ' + orgName + ' at ' + siteCode + '?' +
        ' This sharer is currently billed ' + legacyBill + ' (legacy).<br/>' +
        'Fees will recalculate in the background — this may take a moment.'

      this.$confirm(message, 'Assign rate', {
        confirmButtonText: 'Assign rate',
        cancelButtonText: 'Cancel',
        dangerouslyUseHTMLString: true,
        center: true,
        type: 'warning'
      }).then(function () {
        var formData = {
          rate_id: self.rateId,
          site_id: self.row.site && self.row.site.id,
          organisation_id: self.row.organisation && self.row.organisation.id
        }

        self.saveForm(
          self,
          '/licensees',
          self.row.licensee_id,
          formData,
          'licensee',
          function (data) {
            // Success: emit resolved, close the popover.
            // The audittable tableRef makes saveForm fire the refresh-table event
            // so the resolved row drops on re-fetch — do NOT splice rows manually.
            self.$emit('resolved')
            if (self.$refs.popover) {
              self.$refs.popover.doClose()
            }
          },
          null,   // failCallback — saveForm already toasts on error; keep popover open
          true,   // showMessage
          'audittable'
        )
      }).catch(function () {
        // Confirm cancelled — no-op
      })
    },

    // Phase 1: set agreement start date (no_start_anchor).
    // DateInput emits a JS Date — convert with formatDateSql (→ YYYY-MM-DD).
    setStarted: function () {
      if (!this.startedDate) return
      var self = this
      var dateStr = this.formatDateSql(this.startedDate)
      this.doReconcile({
        title: 'Set start date',
        message: 'Set the agreement start date to ' + dateStr + '? Fees will recalculate in the background — this may take a moment.',
        confirmText: 'Set start date',
        type: 'warning',
        formData: {
          started: dateStr,
          site_id: self.row.site && self.row.site.id,
          organisation_id: self.row.organisation && self.row.organisation.id
        }
      })
    },

    // Phase 1: set billing start date (no_billing_start).
    setBillingStart: function () {
      if (!this.billingStartDate) return
      var self = this
      var dateStr = this.formatDateSql(this.billingStartDate)
      this.doReconcile({
        title: 'Set billing start',
        message: 'Set the billing start date to ' + dateStr + '? Fees will recalculate in the background — this may take a moment.',
        confirmText: 'Set billing start',
        type: 'warning',
        formData: {
          startBilling: dateStr,
          site_id: self.row.site && self.row.site.id,
          organisation_id: self.row.organisation && self.row.organisation.id
        }
      })
    },

    // Phase 1: extend agreement expiry (expired_agreement / expiring_soon).
    extendExpiry: function () {
      if (!this.expiryDate) return
      var self = this
      var dateStr = this.formatDateSql(this.expiryDate)
      this.doReconcile({
        title: 'Extend agreement',
        message: 'Extend the agreement expiry to ' + dateStr + '? Fees will recalculate in the background — this may take a moment.',
        confirmText: 'Extend',
        type: 'warning',
        formData: {
          expiry: dateStr,
          site_id: self.row.site && self.row.site.id,
          organisation_id: self.row.organisation && self.row.organisation.id
        }
      })
    },

    // Phase 1: soft-end terminate (expired_agreement / expiring_soon).
    // Billing stops forward, row preserved — NOT a destroy().
    softTerminate: function () {
      var self = this
      var today = this.formatDateSql(new Date())
      this.doReconcile({
        title: 'Terminate licensee',
        message: 'Soft-terminate this licensee as of today (' + today + ')? Billing will stop going forward. Fees will recalculate in the background — this may take a moment.',
        confirmText: 'Terminate',
        type: 'warning',
        formData: {
          status: 'Terminated',
          terminated: today,
          site_id: self.row.site && self.row.site.id,
          organisation_id: self.row.organisation && self.row.organisation.id
        }
      })
    },

    // Phase 2: clear rate override (empty_override / override_without_rate).
    // DELETE /licensees/{id}/rate-override — mirrors FinanceSharer.clearRateOverride.
    clearOverride: function () {
      var self = this
      var orgName = this.row.organisation && this.row.organisation.name
      var siteId = this.row.site && this.row.site.siteId

      this.$confirm(
        'Clear the rate override for ' + orgName + ' at ' + siteId + '? Billing resumes from the calculated rate. Fees will recalculate in the background — this may take a moment.',
        'Clear rate override',
        {
          confirmButtonText: 'Clear override',
          cancelButtonText: 'Cancel',
          type: 'warning',
          center: true
        }
      ).then(function () {
        self.send(
          self,
          self.getEndpoint('/licensees/' + self.row.licensee_id + '/rate-override'),
          null,
          'Rate override',
          function (data) {
            self.$emit('resolved')
            if (self.$refs.popover) self.$refs.popover.doClose()
          },
          null,
          axios.delete,
          true,
          'Save',
          'audittable',
          true
        )
      }).catch(function () {
        // Confirm cancelled — no-op
      })
    },

    // Phase 2: set termination end date (terminated_no_end).
    // The licensee is already Terminated — this fills in the missing end date.
    setEndDate: function () {
      if (!this.endedDate) return
      var self = this
      var dateStr = this.formatDateSql(this.endedDate)
      this.doReconcile({
        title: 'Set end date',
        message: 'Set the termination end date to ' + dateStr + '? Fees will recalculate in the background — this may take a moment.',
        confirmText: 'Set end date',
        type: 'warning',
        formData: {
          terminated: dateStr,
          site_id: self.row.site && self.row.site.id,
          organisation_id: self.row.organisation && self.row.organisation.id
        }
      })
    }
  },

  data: function () {
    return {
      // no_rate
      rateId: (this.row && this.row.suggestedRate) ? this.row.suggestedRate.id : null,
      // no_start_anchor
      startedDate: null,
      // no_billing_start
      billingStartDate: null,
      // expired_agreement / expiring_soon
      expiryDate: null,
      // terminated_no_end
      endedDate: null
    }
  },

  watch: {
    // Re-seed rateId if the row prop changes (e.g. after a table refresh).
    'row.suggestedRate': function (val) {
      if (val && !this.rateId) {
        this.rateId = val.id
      }
    }
  }
}
</script>

<style scoped>
.audit-reconcile-cell__readonly {
  color: #909399;
  font-size: 16px;
}

.audit-reconcile-cell__popover {
  padding: 4px 0;
}

.audit-reconcile-cell__block + .audit-reconcile-cell__block {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}

.audit-reconcile-cell__label {
  font-weight: 600;
  margin: 0 0 8px;
  font-size: 13px;
}

.audit-reconcile-cell__suggested {
  margin-bottom: 8px;
}

.audit-reconcile-cell__footer {
  margin-top: 10px;
  text-align: right;
}

.audit-reconcile-cell__footer--spread {
  display: flex;
  justify-content: space-between;
}

.audit-reconcile-cell__hint {
  color: #909399;
  font-size: 12px;
  margin: 0 0 6px;
}

.audit-reconcile-cell__block--advisory {
  background: #f5f7fa;
  border-radius: 4px;
  padding: 8px 10px;
}

.audit-reconcile-cell__label--muted {
  color: #909399;
  font-weight: 600;
}

.audit-reconcile-cell__advisory-list {
  margin: 4px 0 0;
  padding-left: 16px;
  font-size: 12px;
  color: #606266;
}

.audit-reconcile-cell__advisory-item {
  margin-bottom: 4px;
}

.audit-reconcile-cell__advisory-footer {
  margin-top: 8px;
}

.audit-reconcile-cell__open-site {
  font-size: 12px;
  color: #409eff;
  text-decoration: none;
}

.audit-reconcile-cell__open-site:hover {
  text-decoration: underline;
}
</style>
